import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { 
    fetchDailyReport,
    fetchWeeklyReport,
    fetchMonthlyReport,
    fetchFileBasedReport,
    fetchFilesForWeek
} from '../actions/reportActions';
import { ReportData, PaginationResponse, FileInfo } from '../../types/report.types';

export interface ReportSliceState {
    reports: ReportData[];
    loading: boolean;
    error: string | null;
    currentReport: ReportData | null;
    searchResults: ReportData[];
    pagination: PaginationResponse;
    availableFiles: FileInfo[];
}

const initialState: ReportSliceState = {
    reports: [],
    loading: false,
    error: null,
    currentReport: null,
    searchResults: [],
    pagination: {
        page: 1,
        per_page: 10,
        total_items: 0,
        total_pages: 1,
        sort_field: 'ProcessDate',
        sort_direction: 'desc'
    },
    availableFiles: []
};

const reportSlice = createSlice({
    name: 'reports',
    initialState,
    reducers: {
        clearReports: (state) => {
            state.reports = [];
            state.currentReport = null;
            state.error = null;
        },
        setPage: (state, action) => {
            state.pagination.page = action.payload;
        },
        setItemsPerPage: (state, action) => {
            state.pagination.per_page = action.payload;
            state.pagination.page = 1;
        },
        setSort: (state, action: PayloadAction<{ field: string; direction: 'asc' | 'desc' }>) => {
            console.log('Sort action triggered:', action.payload);
            console.log('Previous state:', {
                sort_field: state.pagination.sort_field,
                sort_direction: state.pagination.sort_direction,
                currentReport: state.currentReport ? {
                    recordCount: state.currentReport.detailed_records.length,
                    firstRecord: state.currentReport.detailed_records[0]
                } : null
            });
            
            state.pagination.sort_field = action.payload.field;
            state.pagination.sort_direction = action.payload.direction;
            state.pagination.page = 1;
            // Don't clear current report, let the new data replace it
        }
    },
    extraReducers: (builder) => {
        // Daily Report
        builder.addCase(fetchDailyReport.pending, (state) => {
            console.log('Daily Report Fetch - Pending');
            state.loading = true;
            state.error = null;
        });
        builder.addCase(fetchDailyReport.fulfilled, (state, action) => {
            console.log('Daily Report Fetch - Fulfilled', {
                receivedRecords: action.payload.data.detailed_records.length,
                firstRecord: action.payload.data.detailed_records[0]
            });
            state.loading = false;
            state.currentReport = action.payload.data;
            state.pagination = {
                ...action.payload.pagination,
                sort_field: state.pagination.sort_field,
                sort_direction: state.pagination.sort_direction
            };
        });

        // Weekly Report
        builder.addCase(fetchWeeklyReport.pending, (state) => {
            console.log('Weekly Report Fetch - Pending');
            state.loading = true;
            state.error = null;
        });
        builder.addCase(fetchWeeklyReport.fulfilled, (state, action) => {
            console.log('Weekly Report Fetch - Fulfilled', {
                receivedRecords: action.payload.data.detailed_records.length,
                firstRecord: action.payload.data.detailed_records[0]
            });
            state.loading = false;
            state.currentReport = action.payload.data;
            state.pagination = {
                ...action.payload.pagination,
                sort_field: state.pagination.sort_field,
                sort_direction: state.pagination.sort_direction
            };
        });

        // Monthly Report
        builder.addCase(fetchMonthlyReport.pending, (state) => {
            console.log('Monthly Report Fetch - Pending');
            state.loading = true;
            state.error = null;
        });
        builder.addCase(fetchMonthlyReport.fulfilled, (state, action) => {
            console.log('Monthly Report Fetch - Fulfilled', {
                receivedRecords: action.payload.data.detailed_records.length,
                firstRecord: action.payload.data.detailed_records[0]
            });
            state.loading = false;
            state.currentReport = action.payload.data;
            state.pagination = {
                ...action.payload.pagination,
                sort_field: state.pagination.sort_field,
                sort_direction: state.pagination.sort_direction
            };
        });

        // File Based Report
        builder.addCase(fetchFileBasedReport.pending, (state) => {
            console.log('File Based Report Fetch - Pending');
            state.loading = true;
            state.error = null;
        });
        builder.addCase(fetchFileBasedReport.fulfilled, (state, action) => {
            console.log('File Based Report Fetch - Fulfilled', {
                receivedRecords: action.payload.data.detailed_records.length,
                firstRecord: action.payload.data.detailed_records[0]
            });
            state.loading = false;
            // Ensure we completely replace the current report
            state.currentReport = action.payload.data;
            state.pagination = {
                ...action.payload.pagination,
                sort_field: state.pagination.sort_field,
                sort_direction: state.pagination.sort_direction
            };
        });

        // Files List
        builder.addCase(fetchFilesForWeek.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(fetchFilesForWeek.fulfilled, (state, action) => {
            state.loading = false;
            state.availableFiles = action.payload.files || [];
            state.error = null;
        });
        builder.addCase(fetchFilesForWeek.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || 'Failed to fetch files list';
            state.availableFiles = [];
        });
    }
});

export const { clearReports, setPage, setItemsPerPage, setSort } = reportSlice.actions;
export default reportSlice.reducer;